import { useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import TextButton from 'LuxKit/Button/TextButton'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'

interface Props {
  type: 'soldOut' | 'limited'
  variant: 'small' | 'medium' | 'large'
  isPackageOptionTile?: boolean
}

function TourV2DepartureCta(props: Props) {
  const {
    type,
    isPackageOptionTile = false,
    variant,
  } = props

  const intl = useIntl()
  const supportContact = useContext(ContactContext)
  const contact = supportContact.defaultContact

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)

  const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type="TOURS" />) }

  const soldOutMessagePackage = intl.formatMessage({
    id: 'Lnzc9AZhG8PqYMQrPHRB',
    defaultMessage: 'This departure is sold out. Call us for more tour options.',
    description: 'Sold out message for package option tile on the tour page',
  })

  const soldOutMessageDeparture = intl.formatMessage({
    id: 'JYWDKUhVxjG-cIvJxvzr',
    defaultMessage: 'This departure is sold out. Try a different departure or call us for more tour options.',
    description: 'Sold out message for departure tile on the tour offer detail page',
  })

  const limitedMessagePackage = intl.formatMessage(
    {
      id: 'Wkix-JG4EaVmcT6CFlYH',
      defaultMessage: 'This departure has <bold>limited availability</bold>. Call us for more options.',
      description: 'Limited availability message for package option tile',
    },
    {
      bold: (chunks: React.ReactNode) => <b key="bold">{chunks}</b>,
    },
  )

  const limitedMessageDeparture = intl.formatMessage(
    {
      id: 'EKfBLPzf8ropRI5RJ3PI',
      defaultMessage: 'This departure has <bold>limited availability</bold>. Try a different departure or call us for more options.',
      description: 'Limited availability message for departure tile component',
    },
    {
      bold: (chunks: React.ReactNode) => <b key="bold">{chunks}</b>,
    },
  )

  return <Group gap={12} desktopDirection="vertical" direction="vertical" fullWidth>
    {type === 'soldOut' &&
      <BodyTextBlock variant={variant} colour="neutral-two" startIcon={<LineExclamationCircleIcon />}>
        {isPackageOptionTile ? soldOutMessagePackage : soldOutMessageDeparture}
      </BodyTextBlock>}

    {type === 'limited' &&
      <BodyTextBlock variant={variant} colour="neutral-two" startIcon={<LineExclamationTriangleIcon />}>
        {isPackageOptionTile ? limitedMessagePackage : limitedMessageDeparture}
      </BodyTextBlock>}
    <Group desktopDirection="horizontal-reverse" direction="vertical" gap={12} verticalAlign="center" horizontalAlign="center" desktopHorizontalAlign="space-between" fullWidth>

      <TextButton
        fit={isPackageOptionTile ? 'full-width' : 'mobile-full-width'}
        size="medium"
        kind="secondary"
        href={`tel:${contact.number}`}
      >
        {intl.formatMessage(
          {
            id: 'YkU-idVsZGGPI9lLOxvd',
            defaultMessage: 'Call on {contactNumber}',
            description: 'Button text with the phone number to call the customer support contact line',
          },
          { contactNumber: contact.humanReadable },
        )}
      </TextButton>
      {callbacksFeatureEnabled && <TextLink
        size="medium"
        weight="bold"
        onClick={handleCallbackClick}
        startIcon={<LineIncomingCallIcon />}
      >
        {intl.formatMessage({
          id: '7T995HsSRCTu9P5NEkPc',
          defaultMessage: 'Request a callback',
          description: 'Link text allowing the user to request a callback from the customer support team',
        })}
      </TextLink>}
    </Group>
  </Group>
}

export default TourV2DepartureCta
